import { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import { CloseIcon } from "icons";
import { colors } from "theme/palette";
import { useCaching } from "utils/common";
import { AI_SUMMARY_ALLOWED_FILE_TYPES } from "utils/constants";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import IconButton from "components/IconButton";
import Paper from "components/Paper";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import UploadButton from "components/UploadButton";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import DocumentTypeSelect from "entities/AISummaries/components/DocumentTypeSelect";
import {
  openAIDocumentAssistantCreate,
  useOpenAIDocumentTypes,
} from "entities/AISummaries/sdk";
import { IFile, upload } from "entities/File/sdk";

export interface ICreateAISummariesDialog {
  isOpen: boolean;
  dealId: number;
  onClose: () => void;
  onCreate: ({
    openAIDocumentAssistantId,
  }: {
    openAIDocumentAssistantId: number;
  }) => void;
  file?: IFile;
}

const CreateAISummariesDialog = ({
  isOpen: initialIsOpen,
  dealId,
  onClose,
  onCreate,
  file,
}: ICreateAISummariesDialog) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useCaching(initialIsOpen);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<
    number | undefined
  >(undefined);
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([]);

  const [showLoading, hideLoading] = useLoadingBackdrop();

  const { data: documentTypes, isLoading: documentTypesAreLoading } =
    useOpenAIDocumentTypes();

  const handleChangeDocumentType = useCallback(
    ({ newDocumentTypeId }) => setSelectedDocumentTypeId(newDocumentTypeId),
    []
  );

  const handleUploadFile = useCallback(
    ({ files }: { files: Array<File> }) => {
      const allowedFiles = files.filter((item) =>
        AI_SUMMARY_ALLOWED_FILE_TYPES.includes(item.type)
      );

      setSelectedFiles((prevFiles) => [...prevFiles, ...allowedFiles]);

      files
        .filter((item) => !AI_SUMMARY_ALLOWED_FILE_TYPES.includes(item.type))
        .forEach((item) => {
          toast.errorMessage(
            `File ${item?.name} is not a PDF, EXCEL or DOCX file!`
          );
        }, []);
    },
    [setSelectedFiles]
  );

  const onCreateAISummaries = useCallback(
    ({
      fileIds,
      documentTypeId,
    }: {
      fileIds: Array<number>;
      documentTypeId: number;
    }) => {
      showLoading();
      const toastId = toast.loading("Uploading files...");

      openAIDocumentAssistantCreate({
        fileIds,
        dealId,
        documentTypeId,
      })
        .then(({ id: openAIDocumentAssistantId }: { id: number }) => {
          toast.update(toastId, {
            render: "Files have been uploaded successfully!",
            type: "success",
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            isLoading: false,
            closeButton: true,
          });
          setIsSubmitting(false);
          hideLoading();
          onCreate({ openAIDocumentAssistantId });
          setSelectedDocumentTypeId(undefined);
          setSelectedFiles([]);
        })
        .catch((error) => {
          setIsSubmitting(false);
          hideLoading();
          toast.update(toastId, {
            autoClose: 1,
            hideProgressBar: true,
            isLoading: false,
          });
          handleInvalidRequest(error);
        });
    },
    [onCreate, showLoading, hideLoading, dealId]
  );

  const handleCreate = useCallback(() => {
    if (!_.isUndefined(selectedDocumentTypeId)) {
      setIsSubmitting(true);
      if (!_.isUndefined(file) && _.isEmpty(selectedFiles)) {
        onCreateAISummaries({
          fileIds: [file.id],
          documentTypeId: selectedDocumentTypeId,
        });
      } else if (
        _.isUndefined(file) &&
        !_.isEmpty(selectedFiles) &&
        selectedFiles.every((item) =>
          AI_SUMMARY_ALLOWED_FILE_TYPES.includes(item.type)
        )
      ) {
        const fileUploads = selectedFiles.map((selectedFile) =>
          upload({ file: selectedFile, isPrivate: false })
        );

        Promise.all(fileUploads)
          .then((fileIds) => {
            onCreateAISummaries({
              fileIds: fileIds,
              documentTypeId: selectedDocumentTypeId,
            });
          })
          .catch((error) => {
            setIsSubmitting(false);
            hideLoading();
            handleInvalidRequest(error);
          });
      }
    }
  }, [
    selectedDocumentTypeId,
    file,
    selectedFiles,
    onCreateAISummaries,
    hideLoading,
  ]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose();
    setSelectedDocumentTypeId(undefined);
    setSelectedFiles([]);
  }, [onClose, setIsOpen]);

  const isSubmitButtonDisabled = useMemo(
    () =>
      isSubmitting ||
      _.isUndefined(selectedDocumentTypeId) ||
      (_.isUndefined(file) && _.isEmpty(selectedFiles)) ||
      (_.isUndefined(file) &&
        !_.isEmpty(selectedFiles) &&
        !selectedFiles.every((selectedFile) =>
          AI_SUMMARY_ALLOWED_FILE_TYPES.includes(selectedFile.type)
        )),
    [isSubmitting, selectedDocumentTypeId, file, selectedFiles]
  );

  return (
    <Dialog open={isOpen} maxWidth="sm" onClose={handleClose}>
      <DialogTitle sx={{ padding: 2 }}>
        <Paper sx={{ backgroundColor: colors.blue10, borderRadius: "4px" }}>
          <Text variant="h2">Create AI summary</Text>
          <Text variant="text2" color={colors.gray80} marginTop={1}>
            Analyze your documents using carefully pre-engineered prompt
            questions.
          </Text>
        </Paper>
      </DialogTitle>
      <DialogContent sx={{ padding: 2 }}>
        <Stack spacing={3}>
          <Stack spacing={0.5}>
            <Text variant="label" color={colors.gray60} fontWeight={600}>
              Selected files
            </Text>
            {!isOpen && <Skeleton height={56} />}

            {isOpen && _.isUndefined(file) && (
              <Stack spacing={2}>
                <span>
                  <UploadButton
                    accept={AI_SUMMARY_ALLOWED_FILE_TYPES.join(",")}
                    onChange={({ files }: { files: Array<File> }) =>
                      handleUploadFile({ files })
                    }
                    dataTestid="upload-deal-cover-image-button"
                  />
                </span>

                {selectedFiles.map((selectedFile) => (
                  <Tooltip title={selectedFile.name}>
                    <Stack
                      spacing={1}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      flex={1}
                    >
                      <Text variant="text2">
                        {_.truncate(selectedFile.name, {
                          length: 60,
                        })}
                      </Text>
                      <IconButton
                        onClick={() =>
                          setSelectedFiles((prevValue) =>
                            _.xor(prevValue, [selectedFile])
                          )
                        }
                      >
                        <CloseIcon />
                      </IconButton>
                    </Stack>
                  </Tooltip>
                ))}
              </Stack>
            )}

            {isOpen && !_.isUndefined(file) && (
              <Text
                variant="text2"
                color={colors.gray80}
                sx={{
                  height: "50px",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                {file.original_file_name}
              </Text>
            )}
          </Stack>
          {documentTypesAreLoading || _.isNil(documentTypes) ? (
            <Skeleton height="56px" />
          ) : (
            <DocumentTypeSelect
              documentTypes={documentTypes}
              value={selectedDocumentTypeId?.toString()}
              onChange={handleChangeDocumentType}
              showConfirmation={false}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button variant="text" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleCreate} disabled={isSubmitButtonDisabled}>
          Create AI summary
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAISummariesDialog;
